// ABOUTME: Selection step for report wizard - allows selecting pins and investigations.
// ABOUTME: Two-panel layout: select investigations first, then pins become available for selected investigations.

import { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Pin as PinIcon,
  MessageSquare,
  Check,
  BarChart3,
  Table,
  FileText,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { useApp } from '@/context/AppContext';
import { InsightPreviewModal } from '@/components/layout/InsightPreviewModal';
import type { Pin, InvestigationListItem } from '@/types/api';
import type { PinnedItem } from '@/types';

interface WizardSelectionStepProps {
  selectedPinIds: string[];
  selectedInvestigationIds: string[];
  onPinToggle: (id: string) => void;
  onInvestigationToggle: (id: string) => void;
  onPinsLoaded?: (pins: Pin[]) => void;
  onInvestigationsLoaded?: (investigations: InvestigationListItem[]) => void;
}

export function WizardSelectionStep({
  selectedPinIds,
  selectedInvestigationIds,
  onPinToggle,
  onInvestigationToggle,
  onPinsLoaded,
  onInvestigationsLoaded,
}: WizardSelectionStepProps) {
  // Get local pins from AppContext (includes demo pins during tour)
  const { pinnedItems } = useApp();

  const [apiPins, setApiPins] = useState<Pin[]>([]);
  const [investigations, setInvestigations] = useState<InvestigationListItem[]>([]);
  const [pinSearch, setPinSearch] = useState('');
  const [invSearch, setInvSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewPin, setPreviewPin] = useState<PinnedItem | null>(null);

  // Merge API pins with local pins (for demo pins during tour)
  const pins = useMemo(() => {
    // Convert local PinnedItems to Pin format
    // Note: PinnedItem.type may include python-viz types not in PinType, but that's OK for display
    const localPins = pinnedItems.map((item) => ({
      id: item.id,
      title: item.title,
      type: item.type as Pin['type'],
      message_id: item.messageId,
      insight_id: item.insightId,
      source_investigation_id: item.sourceInvestigationId,
      source_investigation_title: item.sourceInvestigationTitle,
      pinned_at: item.timestamp.toISOString(),
      data: (item.data ?? {}) as Record<string, unknown>,
      insight_overview: item.insightOverview ?? null,
    })) as Pin[];

    // Merge: API pins first, then add any local-only pins (demo pins)
    const apiPinIds = new Set(apiPins.map((p) => p.id));
    const localOnlyPins = localPins.filter((p) => !apiPinIds.has(p.id));

    return [...apiPins, ...localOnlyPins];
  }, [apiPins, pinnedItems]);

  // Fetch pins and investigations on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [pinsResponse, investigationsResponse] = await Promise.all([
          apiClient.pins.list({ limit: 100 }),
          apiClient.investigations.list({ limit: 100 }),
        ]);

        setApiPins(pinsResponse.pins);
        setInvestigations(investigationsResponse.investigations);

        // Notify parent of loaded pins (merged) and investigations
        // Note: onPinsLoaded will be called with merged pins in useEffect below
        onInvestigationsLoaded?.(investigationsResponse.investigations);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load pins and investigations');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Notify parent when merged pins change
  useEffect(() => {
    if (!loading && pins.length > 0) {
      onPinsLoaded?.(pins);
    }
  }, [pins, loading, onPinsLoaded]);

  // Group pins by investigation
  const pinsByInvestigation = useMemo(() => {
    const grouped: Record<string, Pin[]> = {};
    for (const pin of pins) {
      const invId = pin.source_investigation_id;
      if (!grouped[invId]) {
        grouped[invId] = [];
      }
      grouped[invId].push(pin);
    }
    return grouped;
  }, [pins]);

  // Get pins available for selection (only from selected investigations)
  const availablePins = useMemo(() => {
    return pins.filter((pin) =>
      selectedInvestigationIds.includes(pin.source_investigation_id)
    );
  }, [pins, selectedInvestigationIds]);

  // Filter pins and investigations by search
  const filteredPins = availablePins.filter((pin) =>
    pin.title.toLowerCase().includes(pinSearch.toLowerCase())
  );

  const filteredInvestigations = investigations.filter((inv) =>
    inv.title.toLowerCase().includes(invSearch.toLowerCase())
  );

  // Get pin count for an investigation
  const getPinCountForInvestigation = (invId: string) => {
    return pinsByInvestigation[invId]?.length || 0;
  };

  // Convert Pin to PinnedItem for preview modal
  const pinToPinnedItem = (pin: Pin): PinnedItem => ({
    id: pin.id,
    title: pin.title,
    type: pin.type,
    timestamp: new Date(pin.pinned_at),
    data: pin.data,
    insightOverview: pin.insight_overview ?? undefined,
    messageId: pin.message_id,
    insightId: pin.insight_id,
    sourceInvestigationId: pin.source_investigation_id,
    sourceInvestigationTitle: pin.source_investigation_title,
  });

  // Handle preview button click
  const handlePreview = (pin: Pin, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the pin toggle
    setPreviewPin(pinToPinnedItem(pin));
  };

  // Get icon for pin type
  const getPinIcon = (type: Pin['type'], small = false) => {
    const size = small ? 'w-3 h-3' : 'w-3.5 h-3.5';
    switch (type) {
      case 'chart':
        return <BarChart3 className={size} />;
      case 'table':
        return <Table className={size} />;
      case 'text':
        return <FileText className={size} />;
      default:
        return <PinIcon className={size} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-error-light border border-error/30 rounded-sm">
        <p className="text-sm text-error">{error}</p>
      </div>
    );
  }

  const totalSelected = selectedPinIds.length + selectedInvestigationIds.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-surface-800 mb-2">
          Select Context
        </h2>
        <p className="text-sm text-surface-600">
          Select investigations first, then choose which pins from those investigations to include.
          {totalSelected > 0 && (
            <span className="ml-2 text-primary-600 font-medium">
              {totalSelected} selected
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Investigations Panel (LEFT) */}
        <div className="space-y-4" data-tour="wizard-investigations">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-surface-800 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-accent-600" />
              Investigations
              {selectedInvestigationIds.length > 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-accent-50 text-accent-600">
                  {selectedInvestigationIds.length}
                </span>
              )}
            </h3>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
            <Input
              placeholder="Search investigations..."
              value={invSearch}
              onChange={(e) => setInvSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
            {filteredInvestigations.length > 0 ? (
              filteredInvestigations.map((inv) => (
                <div
                  key={inv.id}
                  className={`p-2.5 rounded-sm cursor-pointer transition-colors border ${
                    selectedInvestigationIds.includes(inv.id)
                      ? 'border-accent-600 bg-accent-50'
                      : 'border-surface-200 bg-surface-50 hover:bg-surface-100'
                  }`}
                  onClick={() => onInvestigationToggle(inv.id)}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${
                        selectedInvestigationIds.includes(inv.id)
                          ? 'bg-accent-600 text-white'
                          : 'bg-surface-100 text-surface-500'
                      }`}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-surface-800 truncate">
                        {inv.title}
                      </h4>
                      <p className="text-xs text-surface-500">
                        {inv.message_count} messages •{' '}
                        {new Date(inv.updated_at).toLocaleDateString()}
                        {getPinCountForInvestigation(inv.id) > 0 && (
                          <span className="ml-1">
                            • {getPinCountForInvestigation(inv.id)} pin{getPinCountForInvestigation(inv.id) !== 1 ? 's' : ''}
                          </span>
                        )}
                      </p>
                    </div>
                    {selectedInvestigationIds.includes(inv.id) ? (
                      <Check className="w-3.5 h-3.5 text-accent-600 flex-shrink-0" />
                    ) : getPinCountForInvestigation(inv.id) > 0 ? (
                      <ChevronRight className="w-3.5 h-3.5 text-surface-500 flex-shrink-0" />
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6">
                <MessageSquare className="w-6 h-6 text-surface-500 mx-auto mb-2" />
                <p className="text-sm text-surface-600">
                  {invSearch
                    ? 'No investigations match your search'
                    : 'No investigations available'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Pins Panel (RIGHT) */}
        <div className="space-y-4" data-tour="wizard-pins">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-surface-800 flex items-center gap-2">
              <PinIcon className="w-4 h-4 text-primary-600" />
              Available Pins
              {selectedPinIds.length > 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-primary-50 text-primary-600">
                  {selectedPinIds.length}
                </span>
              )}
              {availablePins.length > 0 && selectedPinIds.length === 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-surface-100 text-surface-500">
                  {availablePins.length} available
                </span>
              )}
            </h3>
          </div>

          {selectedInvestigationIds.length === 0 ? (
            // No investigations selected - show prompt
            <div className="border border-dashed border-surface-300 rounded-sm p-8 text-center">
              <PinIcon className="w-8 h-8 text-surface-500 mx-auto mb-3" />
              <p className="text-sm text-surface-600 mb-1">
                Select an investigation first
              </p>
              <p className="text-xs text-surface-500">
                Pins from selected investigations will appear here
              </p>
            </div>
          ) : (
            // Investigations selected - show pins grouped by investigation
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                <Input
                  placeholder="Search pins..."
                  value={pinSearch}
                  onChange={(e) => setPinSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
                {filteredPins.length > 0 ? (
                  // Group pins by investigation when multiple investigations selected
                  selectedInvestigationIds.length > 1 ? (
                    // Multiple investigations - show grouped
                    selectedInvestigationIds.map((invId) => {
                      const inv = investigations.find((i) => i.id === invId);
                      const invPins = filteredPins.filter(
                        (pin) => pin.source_investigation_id === invId
                      );
                      if (invPins.length === 0) return null;

                      return (
                        <div key={invId} className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-surface-500">
                            <MessageSquare className="w-3 h-3" />
                            <span className="font-medium truncate">
                              {inv?.title || 'Unknown Investigation'}
                            </span>
                            <span className="text-surface-400">
                              ({invPins.length} pin{invPins.length !== 1 ? 's' : ''})
                            </span>
                          </div>
                          {invPins.map((pin) => (
                            <div
                              key={pin.id}
                              className={`p-2.5 rounded-sm cursor-pointer transition-colors border ml-3 ${
                                selectedPinIds.includes(pin.id)
                                  ? 'border-primary-600 bg-primary-50'
                                  : 'border-surface-200 bg-surface-50 hover:bg-surface-100'
                              }`}
                              onClick={() => onPinToggle(pin.id)}
                            >
                              <div className="flex items-center gap-2.5">
                                <div
                                  className={`w-6 h-6 rounded-sm flex items-center justify-center flex-shrink-0 ${
                                    selectedPinIds.includes(pin.id)
                                      ? 'bg-primary-600 text-white'
                                      : 'bg-surface-100 text-surface-500'
                                  }`}
                                >
                                  {getPinIcon(pin.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-medium text-surface-800 truncate">
                                    {pin.title}
                                  </h4>
                                  <p className="text-xs text-surface-500 capitalize">
                                    {pin.type}
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => handlePreview(pin, e)}
                                  className="p-1.5 rounded-sm text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors flex-shrink-0 cursor-pointer"
                                  aria-label="Preview pin"
                                  title="Preview"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                {selectedPinIds.includes(pin.id) && (
                                  <Check className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })
                  ) : (
                    // Single investigation - flat list
                    <div className="space-y-2">
                      {filteredPins.map((pin) => (
                        <div
                          key={pin.id}
                          className={`p-2.5 rounded-sm cursor-pointer transition-colors border ${
                            selectedPinIds.includes(pin.id)
                              ? 'border-primary-600 bg-primary-50'
                              : 'border-surface-200 bg-surface-50 hover:bg-surface-100'
                          }`}
                          onClick={() => onPinToggle(pin.id)}
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${
                                selectedPinIds.includes(pin.id)
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-surface-100 text-surface-500'
                              }`}
                            >
                              {getPinIcon(pin.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-surface-800 truncate">
                                {pin.title}
                              </h4>
                              <p className="text-xs text-surface-500 capitalize">
                                {pin.type}
                              </p>
                            </div>
                            <button
                              onClick={(e) => handlePreview(pin, e)}
                              className="p-1.5 rounded-sm text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors flex-shrink-0 cursor-pointer"
                              aria-label="Preview pin"
                              title="Preview"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            {selectedPinIds.includes(pin.id) && (
                              <Check className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <div className="text-center py-6">
                    <PinIcon className="w-6 h-6 text-surface-500 mx-auto mb-2" />
                    <p className="text-sm text-surface-600">
                      {pinSearch
                        ? 'No pins match your search'
                        : 'No pins in selected investigations'}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Selection Summary */}
      {totalSelected > 0 && (
        <div className="bg-surface-0 border border-surface-200 rounded-sm p-4">
          <h4 className="text-sm font-medium text-surface-600 mb-2">
            Selected Context
          </h4>
          <div className="flex flex-wrap gap-2">
            {selectedPinIds.map((id) => {
              const pin = pins.find((p) => p.id === id);
              return pin ? (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-primary-50 text-primary-600"
                >
                  {getPinIcon(pin.type, true)}
                  {pin.title}
                </span>
              ) : null;
            })}
            {selectedInvestigationIds.map((id) => {
              const inv = investigations.find((i) => i.id === id);
              return inv ? (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-accent-50 text-accent-600"
                >
                  <MessageSquare className="w-3 h-3" />
                  {inv.title}
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Insight Preview Modal */}
      <InsightPreviewModal
        item={previewPin}
        onClose={() => setPreviewPin(null)}
      />
    </div>
  );
}
